import { queryOptions } from '@tanstack/react-query';
import { ResultCallback, StreamingStartUIHandlerProperties } from '../types/obs';
import { ensureOBSPresence } from '../utils/ensureOBSPresence';
import { promisefy } from '../utils/promisefy';

interface StreamingStartAdapter {
  /** Notify OBS that we would like to be notified when the button for streaming start is clicked */
  setStreamingStartUIHandlerProperties(
    properties: StreamingStartUIHandlerProperties,
    callback: ResultCallback<boolean>
  ): void;

  /** Notify OBS that we ARE handling the streaming start UI request
   * Ie. set stream title, etc...
   */
  adviseStreamingStartUIRequestAccepted(callback: ResultCallback<boolean>): void;

  /**
   * Notify OBS that we ARE NOT handling the streaming start
   * UI request and it can proceed with default behavior.
   */
  adviseStreamingStartUIRequestRejected(callback: ResultCallback<boolean>): void;
}

const EVENTS = {
  STREAM_START_REQUESTED: 'hostStreamingStartRequested',
} as const;

type Events = typeof EVENTS;
type ObsEventName = Events[keyof Events];

export function emitStreamStartClicked() {
  window.dispatchEvent(new Event(EVENTS.STREAM_START_REQUESTED));
}

export function createMockStreamingStartAdapter(): StreamingStartAdapter {
  return {
    setStreamingStartUIHandlerProperties(
      properties: StreamingStartUIHandlerProperties,
      callback: ResultCallback<boolean>
    ): void {
      void properties;
      callback(true);
    },

    adviseStreamingStartUIRequestAccepted(callback: ResultCallback<boolean>): void {
      // Simulate acceptance
      callback(true);
    },

    adviseStreamingStartUIRequestRejected(callback: ResultCallback<boolean>): void {
      // Simulate rejection
      callback(true);
    },
  };
}

class StreamingStartService {
  private listeners = new Map<ObsEventName, Set<(event: Event) => void>>();
  private isInitialized = false;

  public readonly EVENTS = EVENTS;

  public readonly streamingStatusQueryOptions = queryOptions({
    queryKey: ['streamingStatus'],
    queryFn: () => this.getStreamingStatus(),
  });

  constructor() {
    if (window.location.pathname === '/') {
      this.init();
    }
  }

  public init() {
    Object.values(EVENTS).forEach((event) => {
      window.addEventListener(event, this.onEvent);
    });
    this.isInitialized = true;
  }

  public async getStreamingStatus() {
    await ensureOBSPresence();
    return promisefy(window.host.getStreamingStatus)();
  }

  private onEvent = async (event: Event) => {
    const listeners = this.listeners.get(event.type as ObsEventName);

    if (!listeners) {
      return;
    }

    for (const listener of listeners) {
      listener(event);
    }
  };

  async setStreamingStartUIHandlerProperties(properties: StreamingStartUIHandlerProperties): Promise<boolean> {
    await ensureOBSPresence();
    return new Promise((resolve, reject) => {
      window.host.setStreamingStartUIHandlerProperties(properties, (result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error('Failed to set streaming start UI handler properties'));
        }
      });
    });
  }

  async adviseStreamingStartUIRequestAccepted(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      window.host.adviseStreamingStartUIRequestAccepted((result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error('Failed to advise streaming start UI request accepted'));
        }
      });
    });
  }

  async adviseStreamingStartUIRequestRejected(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      window.host.adviseStreamingStartUIRequestRejected((result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error('Failed to advise streaming start UI request rejected'));
        }
      });
    });
  }

  /**
   * Listen for an event
   * @param event The event to listen for
   * @param listener The listener to call when the event is emitted
   * @returns A function to remove the listener
   */
  on(event: ObsEventName, listener: (event: Event) => void): () => void {
    if (!this.isInitialized) {
      this.init();
    }

    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }

    this.listeners.get(event)!.add(listener);

    return () => {
      this.listeners.get(event)!.delete(listener);
    };
  }

  dispose() {
    this.isInitialized = false;
    this.adviseStreamingStartUIRequestRejected();
    this.setStreamingStartUIHandlerProperties({
      autoStart: true,
      requestAcknowledgeTimeoutSeconds: 10,
    });
    Object.values(EVENTS).forEach((event) => {
      window.removeEventListener(event, this.onEvent);
    });
  }
}

export const StreamingStart = new StreamingStartService();
